import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { Box } from '@material-ui/core';
import CardIcon from '../../../components/CardIcon';
import Value from '../../../components/Value';
import useEarnings from '../../../hooks/useEarnings';
import { getDisplayBalance } from '../../../utils/formatBalance';
import TokenSymbol from '../../../components/TokenSymbol';
import { AutoCompounder, Bank } from '../../../tomb-finance';
import useNextHarvestTime from '../../../hooks/useNextHarvestTime';
import useHarvestAutoCompounder from '../../../hooks/useHarvestAutoCompounder';

interface HarvestProps {
  bank: AutoCompounder;
}

const Harvest: React.FC<HarvestProps> = ({ bank }) => {
  const { nextHarvestTime, loading } = useNextHarvestTime(bank);
  const { onHarvest } = useHarvestAutoCompounder(bank);
  const [timeRemaining, setTimeRemaining] = useState<string>('');
  const [progress, setProgress] = useState(0);
  const [isHarvesting, setIsHarvesting] = useState(false);

  useEffect(() => {
    const updateCountdown = () => {
      if (!nextHarvestTime) return;

      const now = new Date();
      const diffMs = nextHarvestTime.getTime() - now.getTime();
      const totalDuration = 3 * 60 * 60 * 1000; // 3 hours in ms

      if (diffMs <= 0) {
        setTimeRemaining('Ready');
        setProgress(100);
        return;
      }

      // Calculate time components
      const hours = Math.floor(diffMs / (1000 * 60 * 60));
      const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((diffMs % (1000 * 60)) / 1000);

      // Calculate progress percentage
      const elapsed = totalDuration - diffMs;
      const progressPercent = (elapsed / totalDuration) * 100;
      setProgress(progressPercent);

      setTimeRemaining(`${hours}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`);
    };

    updateCountdown();
    const interval = setInterval(updateCountdown, 1000);

    return () => clearInterval(interval);
  }, [nextHarvestTime]);

  const handleCompound = async () => {
    if (!timeRemaining.includes('Ready')) return;
    
    try {
      setIsHarvesting(true);
      await onHarvest();
    } catch (error) {
      console.error('Failed to compound:', error);
    } finally {
      setIsHarvesting(false);
    }
  };

  return (
    <Box>
      <div className="relative flex flex-col items-center p-4 rounded-xl border bg-card/60 backdrop-blur">
        <StyledCardContentInner>
          <StyledCardHeader>
            <CircularCountdown>
              <CircularProgress progress={progress} />
              {loading ? (
                <CountdownText>Loading...</CountdownText>
              ) : (
                <>
                  <CountdownText>{timeRemaining}</CountdownText>
                  <SubText>Until next compound</SubText>
                </>
              )}
            </CircularCountdown>
          </StyledCardHeader>
          <StyledCardActions>
            <button
              className='tracking-normal inline-flex whitespace-nowrap backdrop-blur items-center justify-center gap-x-1 py-2 px-6 rounded-full font-bold'
              disabled={!timeRemaining.includes('Ready') || isHarvesting}
              onClick={handleCompound}
            >
              {isHarvesting ? 'Compounding...' : 'Compound'}
            </button>
          </StyledCardActions>
        </StyledCardContentInner>
      </div>
    </Box>
  );
};

const CircularCountdown = styled.div`
  position: relative;
  width: 200px;
  height: 200px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  &::before {
    content: '';
    position: absolute;
    width: 100%;
    height: 100%;
    border-radius: 50%;
    border: 1px solid rgba(255, 255, 255, 0.1); // This matches the box border opacity
    box-sizing: border-box;
  }
`;

const CircularProgress = styled.div<{ progress: number }>`
  position: absolute;
  width: 100%;
  height: 100%;
  border-radius: 50%;
  background: conic-gradient(
    from 0deg,
    ${props => `
      ${props.progress <= 25 ? '#214e82' : 
      props.progress <= 50 ? '#506077' :
      props.progress <= 75 ? '#ec5409' : 
      '#ffc966'
    }`} ${props => props.progress}%,
    transparent ${props => props.progress}%
  );
  mask: radial-gradient(transparent 65%, black 66%);
  -webkit-mask: radial-gradient(transparent 65%, black 66%);
  transition: background 0.3s ease;
`;

const CountdownText = styled.div`
  font-size: 36px;
  font-weight: 700;
  background: linear-gradient(270deg, #214e82 25%, #506077 50%, #ec5409 75%, #ffc966);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  z-index: 1;
`;

const SubText = styled.div`
  font-size: 14px;
  color: #506077;
  margin-top: 8px;
  z-index: 1;
`;

const StyledCardHeader = styled.div`
  align-items: center;
  display: flex;
  flex-direction: column;
`;

const StyledCardActions = styled.div`
  display: flex;
  justify-content: center;
  margin-top: ${(props) => props.theme.spacing[6]}px;
  width: 100%;
`;

const StyledCardContentInner = styled.div`
  align-items: center;
  display: flex;
  flex: 1;
  flex-direction: column;
  justify-content: space-between;
`;

export default Harvest;
